/**
 * Mirrors every telemetry emission to memory, downloadable as a file.
 *
 * The four channels (event / metric / log / trace) leave the browser for three
 * different backends, so checking that a change reports what it intends to
 * means waiting on ingestion and then reconstructing one round out of three
 * consoles. This keeps the same payloads locally at the moment they are
 * emitted, giving a single ordered file per run to diff against expectations —
 * including the metric labels, which are the part most easily gotten wrong and
 * the part hardest to read back out of an aggregated histogram.
 *
 * Recording is passive: it never alters what is sent, and a failure to record
 * is swallowed rather than surfaced, because a diagnostic must not be able to
 * break the playback path it is observing.
 *
 * **Disabled by default.** `startTelemetryCapture()` turns it on; a shipped
 * integration pays nothing for it and would never call the download to get the
 * data back out. Nothing leaves the page until `downloadTelemetryRecords()` is
 * called.
 *
 * Aligned with iOS / Android `TelemetryRecorder`.
 */

/**
 * Kept small enough to be irrelevant against playback's own allocations — a
 * round emits on the order of tens of records, so this holds hundreds of rounds
 * and still bounds a long-running session.
 */
const MAX_RECORDS = 5000;

const records: string[] = [];
let dropped = 0;
let isEnabled = false;

/** Begin a fresh capture, discarding anything held from a previous run. */
export function startTelemetryCapture(): void {
  records.length = 0;
  dropped = 0;
  isEnabled = true;
}

/**
 * Stop capturing. Anything already captured stays readable via
 * `getTelemetryRecords` / `downloadTelemetryRecords`.
 */
export function stopTelemetryCapture(): void {
  isEnabled = false;
}

/**
 * Record one emission.
 *
 * @param channel - the transport it goes out on, which is what makes the file
 *   answer "did this land as a metric or only as an event" — a distinction the
 *   payloads themselves do not carry.
 * @internal
 */
export function recordTelemetry(
  channel: string,
  name: string,
  fields: Record<string, unknown>,
): void {
  // Checked before the JSON work, which is the whole cost when disabled.
  if (!isEnabled) return;
  try {
    // Serialise here rather than at download time: a value that cannot be
    // encoded would otherwise surface much later, far from the call site that
    // produced it.
    const line = JSON.stringify({
      ts: Date.now(),
      channel,
      name,
      ...fields,
    });
    if (records.length >= MAX_RECORDS) {
      records.shift();
      dropped++;
    }
    records.push(line);
  } catch {
    // A payload that will not serialise is not worth failing a render over.
  }
}

/**
 * Everything captured so far, as JSONL. The buffer is left intact so a later
 * dump still covers the whole session.
 */
export function getTelemetryRecords(): string {
  const lines = [...records];
  if (dropped > 0) {
    // Stated in the file rather than logged: a reader counting rounds needs to
    // know the head was trimmed.
    lines.unshift(
      JSON.stringify({
        ts: Date.now(),
        channel: 'recorder',
        name: 'records_dropped',
        count: dropped,
      }),
    );
  }
  return lines.length > 0 ? lines.join('\n') + '\n' : '';
}

/** Discard everything captured. Used to isolate a single run. */
export function resetTelemetryRecords(): void {
  records.length = 0;
  dropped = 0;
}

/**
 * Save the capture as a .jsonl file.
 *
 * @returns the filename written, or null when nothing has been captured.
 */
export function downloadTelemetryRecords(): string | null {
  const text = getTelemetryRecords();
  if (!text || typeof document === 'undefined') return null;

  const stamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+$/, 'Z');
  const filename = `rtc_telemetry_${stamp}.jsonl`;
  const url = URL.createObjectURL(
    new Blob([text], { type: 'application/x-ndjson' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}
